import React, { useCallback, useEffect, useState } from 'react';

import { Text, createStyles, useMantineTheme } from '@mantine/core';
import { IconCircleCheckFilled, IconCircleOff } from '@tabler/icons-react';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCommunications from 'hooks/communications/useCommunications';
import { convert } from 'html-to-text';
import { CellProps } from 'react-table';
import { Notification } from 'types';

import { NotificationType } from '../types';

const useStyles = createStyles(() => ({
  clickableRow: {
    cursor: 'pointer',
  },
}));

interface ICommunicationsTableContainerProps {
  searchFilters: {
    date?: [Date | null, Date | null];
    athleteId: string;
    notificationType?: NotificationType;
  };
  columnsOverride?: any;
  timeType?: 'past' | 'upcoming';
  limit?: string;
  defaultEndDate?: string;
  setOrderingValue?: React.Dispatch<React.SetStateAction<string>>;
  margin?: number;
  bgc?: 'white' | 'gray';
  openViewDrawer: (id: string) => void;
}

const CommunicationsTableContainer = ({
  columnsOverride,
  searchFilters,
  openViewDrawer,
  defaultEndDate,
  setOrderingValue,
  margin = 12,
  bgc = 'white',
}: ICommunicationsTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { dateFormat } = useLocalization();

  const [tableData, setTableData] = useState<Notification[]>([]);
  const [ordering, setOrdering] = useState('createdAt');
  const [ascending, setAscending] = useState(false);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useCommunications({
      ...searchFilters,
      createdAtStart:
        searchFilters.date && searchFilters.date[0]
          ? dayjs(searchFilters.date[0]).startOf('day').toISOString()
          : undefined,
      createdAtEnd:
        searchFilters.date && searchFilters.date[1]
          ? dayjs(searchFilters.date[1]).endOf('day').toISOString()
          : defaultEndDate,

      ordering: ascending ? ordering : `-${ordering}`,
      notificationType: searchFilters.notificationType,
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(a => {
        setOrderingValue?.(!a ? header : `-${header}`);
        return !a;
      });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [ascending],
  );

  const renderStatusSuccess = (row: Notification): JSX.Element => {
    if (row.deliverySuccess) {
      return <IconCircleCheckFilled style={{ color: theme.colors.kiloBlue[6] }} />;
    }
    return <IconCircleOff color='gray' />;
  };

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  const columns = [
    {
      Header: 'Date',
      accessor: 'createdAt' as const,
      Cell: ({ value }: CellProps<Notification>) => dayjs(value).format(dateFormat),
    },
    {
      Header: 'Type',
      accessor: 'notificationType' as const,
      disableSortBy: false,
    },
    {
      Header: 'Subject/Title',
      accessor: 'contentPreview' as const,
      Cell: ({ row }: CellProps<Notification>) => <Text truncate>{row.original.contentPreview}</Text>,
      maxWidth: '20vw',
    },
    {
      Header: 'Message',
      accessor: 'contentBody' as const,
      Cell: ({ row }: CellProps<Notification>) => <Text truncate>{convert(row.original.contentBody)}</Text>,
      maxWidth: '30vw',
    },
    {
      Header: 'Status',
      accessor: 'deliverySuccess' as const,
      Cell: ({ row }: CellProps<Notification>) => renderStatusSuccess(row.original),
    },
  ];

  return (
    <Table
      tableData={tableData}
      columns={columnsOverride || columns}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      clickableHeaders={['startDate', 'name']}
      handleHeaderClick={handleHeaderClick}
      ascending={ascending}
      ordering={ordering}
      handleRowClick={(notification: Notification) => openViewDrawer(notification.notificationId)}
      rowClassName={() => classes.clickableRow}
      bgc={bgc}
      margin={margin}
    />
  );
};

export default CommunicationsTableContainer;
